import { isEmpty, isNil } from 'lodash';

import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE
} from 'utils/channels';

import type { Subcampaign } from 'types';

type MetricsType = {
  channel: string,
  followers: number,
  avgEngagementRate: number
};

export function collectMetrics(subcampaigns: Array<Subcampaign>, currentState: string): Array<MetricsType> {
  const supportedChannels = [
    CHANNEL_TYPE_INSTAGRAM,
    CHANNEL_TYPE_TIKTOK,
    CHANNEL_TYPE_YOUTUBE
  ].filter((channel) => {
    if (currentState === 'draft') {
      // for draft push all available channels
      return subcampaigns.some((subcampaign) => {
        const channels = subcampaign.creatorProfile.channels;
        return Object.keys(channels)
          .filter((key) => !isEmpty(channels[key]))
          .includes(channel);
      });
    }

    // for non-draft supported channels are based on task types
    return subcampaigns.some((subcampaign) => {
      const offer = subcampaign.offer;
      const overview = offer.deliverablesOverview ?? {};

      return Object.keys(overview)
        .filter((deliverableType: string) => {
          const hasDeliverablesOfType = (overview[deliverableType]?.length ?? 0) > 0;
          return hasDeliverablesOfType && deliverableType.includes(channel);
        })
        .length > 0;
    });
  });

  const followers = {};
  const engagementRate = {};

  supportedChannels.forEach((channel) => {
    followers[channel] = [];
    engagementRate[channel] = [];
  });

  subcampaigns.forEach((subcampaign) => {
    const channels = subcampaign.creatorProfile?.channels || {};
    supportedChannels.forEach((channel) => {
      const channelMetrics = channels[channel]?.metrics;

      if (channelMetrics && !Array.isArray(channelMetrics)) {
        followers[channel].push(channelMetrics.followers);
        engagementRate[channel].push(channelMetrics.engagement);
      }
    });
  });

  return supportedChannels.map((channel: string) => {
    const channelFollowers = (followers[channel] || []).filter(entry => !isNil(entry));
    const channelEngagement = (engagementRate[channel] || []).filter(entry => !isNil(entry));

    const totalFollowers: number = channelFollowers.reduce((acc, item) => acc + item, 0);
    const avgEngagement = channelEngagement.reduce((acc, item) => acc + item, 0) / channelEngagement.length;

    return ({
      channel,
      followers: totalFollowers,
      avgEngagementRate: avgEngagement
    });
  });
}
